import { useCallback, useRef, useState } from "react";
import toast from "react-hot-toast";
import api from "../../../../chat/api";

/**
 * Owns the Generate Answer / Finalize workflow + its shared `fillStage`.
 *   - Generate Answer: POST /fill/ (async) then poll until status leaves
 *     "filling". Sets `readyToFinalize = true` so the primary action in
 *     fill mode swaps to Finalize. RAG fills skip user_overridden fields.
 *   - Finalize: synchronous POST /finalize/. Merges the response into the
 *     existing job (preserving `fields` since finalize doesn't return them)
 *     so the rendered PDF swaps to `filled_pdf_url` via applyJobData.
 *
 * Note: `fillStage` is shared with `useDownloadJob` for the auto-finalize
 * branch of the download flow, so the loading button label stays in sync.
 */
export default function useFillFinalize({
  jobId,
  job,
  applyJobData,
  pollJobUntilFilled,
  setIsFinalizedView,
}) {
  const [fillStage, setFillStage] = useState("idle");
  const [readyToFinalize, setReadyToFinalize] = useState(false);
  const filling = fillStage !== "idle";
  const cancelRef = useRef(false);

  const handleGenerateAnswer = useCallback(async (fieldIds) => {
    if (filling) return;
    cancelRef.current = false;
    try {
      setFillStage("filling");
      // Scope generation to specific fields (e.g. the current page) when given.
      const body =
        Array.isArray(fieldIds) && fieldIds.length ? { field_ids: fieldIds } : {};
      const fillRes = await api.post(
        `/api/v1/rfp/autopdf/jobs/${jobId}/fill/`,
        body
      );
      if (![200, 201, 202].includes(fillRes.status)) {
        throw new Error("Fill request failed");
      }
      const result = await pollJobUntilFilled({
        isCancelled: () => cancelRef.current,
      });

      if (result?.cancelled) {
        toast("Generation stopped. Already-filled answers are kept.", {
          icon: "✋",
        });
        return;
      }

      setReadyToFinalize(true);
      // If the user is regenerating after a previous finalize, drop the
      // filled view so bbox text overlays come back until they finalize.
      setIsFinalizedView(false);

      toast.success("Answers ready. Review them, then Download.");
    } catch (err) {
      console.error("Generate answer failed:", err);
      const msg =
        err.response?.data?.detail ||
        err.response?.data?.message ||
        err.response?.data?.error ||
        err.message ||
        "Failed to generate answers.";
      toast.error(msg);
    } finally {
      setFillStage("idle");
    }
  }, [filling, jobId, pollJobUntilFilled, setIsFinalizedView]);

  // Stop generation: tell the backend to halt, and abort the local poll so the
  // overlay closes immediately. Fields already filled are kept; re-generating
  // later fills only the remaining (unanswered) ones.
  const cancelGenerate = useCallback(() => {
    cancelRef.current = true;
    api
      .post(`/api/v1/rfp/autopdf/jobs/${jobId}/cancel-fill/`)
      .catch(() => {});
  }, [jobId]);

  const handleFinalize = useCallback(async () => {
    if (filling) return;
    try {
      setFillStage("finalizing");
      const finalizeRes = await api.post(
        `/api/v1/rfp/autopdf/jobs/${jobId}/finalize/`,
        {},
        { timeout: 0 }
      );
      if (![200, 201].includes(finalizeRes.status)) {
        throw new Error("Finalize request failed");
      }
      const finalized = finalizeRes.data;
      if (finalized?.error_message) {
        throw new Error(finalized.error_message);
      }
      if (finalized?.status !== "completed") {
        throw new Error(
          `Finalize returned status "${finalized?.status || "unknown"}", expected "completed"`
        );
      }
      // /finalize/ doesn't return `fields`, so merge with the prior job
      // state to preserve bbox data.
      const merged = {
        ...(job || {}),
        ...finalized,
        fields: finalized?.fields ?? job?.fields ?? [],
      };
      applyJobData(merged);
      // applyJobData already swapped pdfUrl → filled_pdf_url and set
      // isFinalizedView based on the merged job payload.
      setReadyToFinalize(false);

      toast.success("Answers written to PDF!");
    } catch (err) {
      console.error("Finalize failed:", err);
      const msg =
        err.response?.data?.detail ||
        err.response?.data?.message ||
        err.response?.data?.error ||
        err.message ||
        "Failed to finalize PDF.";
      toast.error(msg);
    } finally {
      setFillStage("idle");
    }
  }, [filling, jobId, job, applyJobData]);

  return {
    fillStage,
    setFillStage,
    filling,
    readyToFinalize,
    setReadyToFinalize,
    handleGenerateAnswer,
    cancelGenerate,
    handleFinalize,
  };
}
